import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getStatuses } from '@/api/statuses';
import { FAILURE_STATUSES, type CallStatus } from '@/types/api';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const PIPELINE_ORDER: CallStatus[] = [
  'added',
  'claiming_download',
  'downloaded',
  'claiming_transcribe',
  'transcribed',
  'claiming_title',
  'titled',
  'claiming_jira',
];

const TERMINAL: CallStatus[] = ['finished', 'skipped'];

export default function CountsGrid() {
  const { t: tStatuses } = useTranslation('statuses');
  const { t: tDash } = useTranslation('dashboard');
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['statuses'],
    queryFn: getStatuses,
    refetchInterval: 5_000,
    refetchIntervalInBackground: false,
  });
  const counts = q.data?.counts ?? {};

  const failedTotal = FAILURE_STATUSES.reduce((sum, s) => sum + (counts[s] ?? 0), 0);

  function failedClicks() {
    const params = new URLSearchParams();
    for (const s of FAILURE_STATUSES) params.append('status', s);
    navigate(`/tasks?${params.toString()}`);
  }

  function cell(s: CallStatus, label: string, n: number, tip: string, klass = '') {
    const card = (
      <div
        className={cn(
          'flex flex-col rounded-md border bg-card p-2 text-center',
          klass,
        )}
      >
        <div className="text-[10px] text-muted-foreground">{label}</div>
        <div className="text-lg font-semibold">{n}</div>
      </div>
    );
    if (!tip) return <div key={s}>{card}</div>;
    return (
      <Tooltip key={s}>
        <TooltipTrigger asChild>{card}</TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6 lg:grid-cols-11">
        {PIPELINE_ORDER.map((s) => cell(s, tStatuses(`labels.${s}`), counts[s] ?? 0, tStatuses(`tips.${s}`)))}
        <button
          type="button"
          onClick={failedClicks}
          className={cn(
            'flex flex-col rounded-md border p-2 text-center transition-colors',
            failedTotal > 0
              ? 'border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/20'
              : 'border bg-card text-muted-foreground hover:bg-accent',
          )}
        >
          <div className="text-[10px]">{tDash('counts.failedAggregate')}</div>
          <div className="text-lg font-semibold">{failedTotal}</div>
        </button>
        {TERMINAL.map((s) => cell(s, tStatuses(`labels.${s}`), counts[s] ?? 0, tStatuses(`tips.${s}`)))}
      </div>
    </TooltipProvider>
  );
}
